import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AssistanceService } from '../../services/assistance/assistance.service';
import { IdleService } from '../../services/idle/idle.service';

@Component({
  selector: 'magnifying-glass',
  templateUrl: './magnifying-glass.component.html',
  styleUrls: ['./magnifying-glass.component.scss']
})
export class MagnifyingGlassComponent implements OnInit, OnDestroy {

  @ViewChild('magnifier') magnifier: ElementRef;
  magnifierContent;
  magnifierBody;
  isVisible = false;
  events = {};
  options = Object.assign({ zoom: 1.3, shape: 'square', width: 900, height: 450 });
  MutationObserver = window.MutationObserver;
  observerObj;
  exclude = ['INPUT', 'TEXTAREA', 'SELECT', 'A', 'BUTTON'];
  moveObject: { ofs_x: number, ofs_y: number, pos_x: number, pos_y: number };
  private eventListeners = [];

  constructor(private assistance: AssistanceService, private idle: IdleService) {}

  ngOnInit(): void {
    if (!this.magnifier) {
      setTimeout(() => {
        this.ngOnInit();
      }, 500);
      return;
    }

    this.magnifierContent = this.magnifier.nativeElement.querySelector('.magnifier-content');
    if (window.addEventListener) {
      this.addEventListener(window, 'resize', this.syncContent, false);
      this.addEventListener(window, 'scroll', this.syncScrollBars, true);
    }
    this.makeDraggable(this.magnifier.nativeElement, this.syncViewport);
  }

  ngOnDestroy() {
    this.eventListeners.forEach(listener => {
      listener?.element?.removeEventListener(listener.action, listener.callback);
    });
    this.eventListeners = [];
  }

  addEventListener(element, action, callback, options?) {
    element.addEventListener(action, callback, options);
    this.eventListeners.push({ element, action, callback });
  }

  public show() {
    this.options.zoom = 1.3;
    this.setupMagnifier();
    this.prepareContent();
    this.setPosition(this.magnifier.nativeElement, 200, 200);
    this.magnifier.nativeElement.style.display = 'block';
    this.syncViewport();
    this.initScrollBars();
    this.bindDOMObserver();
    this.isVisible = true;
    setTimeout(() => {
      this.syncContent();
      this.syncScrollBars();
    }, 10);
  }

  public hide() {
    this.unBindDOMObserver();
    if (this.magnifierContent) {
      this.magnifierContent.innerHTML = '';
    }
    if (this.magnifier && this.magnifier.nativeElement) {
      this.magnifier.nativeElement.style.display = 'none';
    }
    this.isVisible = false;
  }

  public close(e: Event) {
    e.stopPropagation();
    this.assistance.toggleMagnifyingGlass(false);
  }

  public zoom(direction = 'in') {
    this.options.zoom = direction === 'in' ? this.options.zoom * 1.2 : this.options.zoom / 1.2;
    this.setupMagnifier();
    this.syncContent();
  }

  private unBindDOMObserver() {
    if (this.observerObj) {
      this.observerObj.disconnect();
      this.observerObj = null;
    }
    if (document.removeEventListener) {
      document.removeEventListener('DOMNodeInserted', this.domChanged, false);
      document.removeEventListener('DOMNodeRemoved', this.domChanged, false);
    }
  }

  private bindDOMObserver() {
    if (this.MutationObserver) {
      this.observerObj = new MutationObserver(mutations => {
        for (let i = 0; i < mutations.length; i++) {
          if (!this.isDescendant(this.magnifier.nativeElement, mutations[i].target)) {
            try {
              this.triggerEvent('checkMutation', mutations[i]);
              this.domChanged();
              break;
            } catch (error) {}
          }
        }
      });
      this.observerObj.observe(document, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'width', 'height', 'style'], attributeOldValue: true
      });
    } else if (document.addEventListener) {
      this.addEventListener(document, 'DOMNodeInserted', this.domChanged, false);
      this.addEventListener(document, 'DOMNodeRemoved', this.domChanged, false);
    }
  }

  private domChanged() {
    if (this.isVisible) {
      this.syncContentQueued();
    }
  }

  private syncContentQueued() {
    if (this.isVisible) {
      setTimeout(() => {
        this.syncContent();
        this.syncScrollBars();
      }, 1000);
    }
  }

  private setupMagnifier() {
    this.setDimensions(this.magnifier.nativeElement, this.options.width, this.options.height);
    this.magnifierContent.style.WebkitTransform = 'scale(' + this.options.zoom + ')';
    this.magnifierContent.style.MozTransform = 'scale(' + this.options.zoom + ')';
    this.magnifierContent.style.OTransform = 'scale(' + this.options.zoom + ')';
    this.magnifierContent.style.MsTransform = 'scale(' + this.options.zoom + ')';
    this.magnifierContent.style.transform = 'scale(' + this.options.zoom + ')';
  }

  private initScrollBars() {
    this.triggerEvent('initScrollBars', this.magnifierBody);
  }

  private syncContent() {
    if (this.isVisible) {
      this.prepareContent();
      this.syncViewport();
      this.syncScrollBars();
    }
  }

  private prepareContent() {
    this.magnifierContent.innerHTML = '';
    const bodyOriginal = document.body;
    const bodyCopy = bodyOriginal.cloneNode(true) as any;
    this.magnifierContent.appendChild(bodyCopy);
    this.setDimensions(this.magnifierContent, document.body.clientWidth, document.body.clientHeight);
    const color = bodyOriginal.style.backgroundColor;
    if (color) {
      this.magnifier.nativeElement.style.backgroundColor = color;
    }

    bodyCopy.style.cursor = 'auto';
    bodyCopy.style.paddingTop = '0';
    bodyCopy.setAttribute('unselectable', 'on');

    const canvasOriginal = bodyOriginal.querySelectorAll('canvas');
    const canvasCopy = bodyCopy.querySelectorAll('canvas');
    if (canvasOriginal.length > 0 && canvasOriginal.length === canvasCopy.length) {
      for (let i = 0; i < canvasOriginal.length; i++) {
        let ctx = canvasCopy[i].getContext('2d');
        ctx.drawImage(canvasOriginal[i], 0, 0);
      }
    }

    this.removeSelectors(bodyCopy, 'script');
    this.removeSelectors(bodyCopy, 'audio');
    this.removeSelectors(bodyCopy, 'video');
    this.removeSelectors(bodyCopy, '.magnifier');

    this.magnifierBody = bodyCopy.querySelector('body');

    setTimeout(() => {
      this.copyScrolls(bodyOriginal, bodyCopy);
    }, 0);

    this.triggerEvent('prepareContent', bodyCopy);
    this.triggerEvent('contentUpdated', this.magnifierBody);
  }

  private copyScrolls(original: HTMLElement, copy: HTMLElement) {
    const origElements = original.querySelectorAll('*');
    const copyElements = copy.querySelectorAll('*');

    for (let i = 0; i < origElements.length; i++) {
      const orig = origElements[i] as HTMLElement;
      const c = copyElements[i] as HTMLElement;
      if (c) {
        c.scrollTop = orig.scrollTop;
        c.scrollLeft = orig.scrollLeft;
      }
    }
  }

  private syncViewport() {
    const left = -this.magnifier.nativeElement.offsetLeft * this.options.zoom - document.body.scrollLeft * this.options.zoom - 125;
    const top = -this.magnifier.nativeElement.offsetTop * this.options.zoom - document.body.scrollTop * this.options.zoom - 75;
    this.setPosition(this.magnifierContent, left, top);
    this.triggerEvent('viewPortChanged', this.magnifierBody);
  }

  private syncScrollBars(e?) {
    if (this.isVisible) {
      if (e && e.target) {
        this.syncScroll(e.target);
      } else {
        const scrolled: Element[] = [];
        const elements = document.querySelectorAll('div');
        for (let i = 0; i < elements.length; i++) {
          if (elements[i].scrollTop > 0) {
            scrolled.push(elements[i]);
          }
        }
        for (let i = 0; i < scrolled.length; i++) {
          if (!this.isDescendant(this.magnifier.nativeElement, scrolled[i])) {
            this.syncScroll(scrolled[i]);
          }
        }
      }
      this.triggerEvent('syncScrollBars', this.magnifierBody);
    }
  }

  private syncScroll(ctrl) {
    if (!ctrl.getAttribute) return false;
    const id = ctrl.getAttribute('data-magnifier-id');
    if (id) {
      const t = this.magnifierBody.querySelector('[data-magnifier-id="' + id + '"]');
      if (t) {
        t.scrollTop = ctrl.scrollTop;
        t.scrollLeft = ctrl.scrollLeft;
        return true;
      }
    } else if (ctrl === document) {
      this.syncViewport();
    }
    return false;
  }

  private removeSelectors(container, selector) {
    const elements = container.querySelectorAll(selector);
    for (let i = 0; i < elements.length; i++) {
      elements[i].parentNode.removeChild(elements[i]);
    }
  }

  private triggerEvent(event, data) {
    const handlers = this.events[event];
    if (handlers) {
      for (let i = 0; i < handlers.length; i++) {
        handlers[i].call(this, data);
      }
    }
  }

  private setDimensions(element, width, height) {
    element.style.width = width + 'px';
    element.style.height = height + 'px';
  }

  private setPosition(element, left, top) {
    element.style.left = left + 'px';
    element.style.top = top + 'px';
  }

  private isDescendant(parent, child) {
    let node = child;
    while (node != null) {
      if (node === parent) return true;
      node = node.parentNode;
    }
    return false;
  }

  private makeDraggable(ctrl, handler) {
    ctrl.style.cursor = 'move';

    this.addEventListener(ctrl, 'mousedown', e => {
      if (this.isVisible) this.downHandler(e);
    });

    this.addEventListener(window, 'mousemove', e => {
      if (this.isVisible) this.moveHandler(e, handler);
    });

    this.addEventListener(window, 'mouseup', () => {
      if (this.isVisible) this.moveObject = null;
    });

    this.addEventListener(ctrl, 'touchstart', e => {
      if (this.isVisible) this.downHandler(e);
    });

    this.addEventListener(window, 'touchmove', e => {
      if (this.isVisible) this.moveHandler(e, handler);
    });

    this.addEventListener(window, 'touchend', () => {
      if (this.isVisible) this.moveObject = null;
    });
  }

  private downHandler(e) {
    this.idle.restartTimeout();
    const target = e.target || e.srcElement;
    if (target && (this.exclude.indexOf(target.tagName.toUpperCase()) === -1)) {
      const pageX = e.pageX || e.touches[0].pageX;
      const pageY = e.pageY || e.touches[0].pageY;

      this.moveObject = {
        ofs_x: this.magnifier.nativeElement.getBoundingClientRect().left - this.magnifier.nativeElement.offsetLeft,
        ofs_y: this.magnifier.nativeElement.getBoundingClientRect().top - this.magnifier.nativeElement.offsetTop,
        pos_x: pageX - (this.magnifier.nativeElement.getBoundingClientRect().left + document.body.scrollLeft),
        pos_y: pageY - (this.magnifier.nativeElement.getBoundingClientRect().top + document.body.scrollTop)
      };
    }
  }

  private moveHandler(e, handler) {
    this.idle.restartTimeout();
    if (this.magnifier.nativeElement !== null && this.moveObject) {
      const pageX = e.pageX || e.touches[0].pageX;
      const pageY = e.pageY || e.touches[0].pageY;
      const left = pageX - this.moveObject.pos_x - this.moveObject.ofs_x - document.body.scrollLeft;
      const top = pageY - this.moveObject.pos_y - this.moveObject.ofs_y - document.body.scrollTop;

      this.setPosition(this.magnifier.nativeElement, left, top);
      if (handler) handler.call(this);
    }
  }
}
